import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { 
  Users, 
  Zap, 
  Brain, 
  MessageSquare, 
  Crown, 
  Check, 
  ArrowRight,
  BarChart3,
  Clock,
  Sparkles,
  ChevronDown,
  Infinity,
  Send,
  Lightbulb
} from 'lucide-react';
import { PollarLogoSVG } from '@/constants/logo';
import { trackPageView, trackClick, EventTypes } from '@/utils/analytics';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const Landing = () => {
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const [activePricing, setActivePricing] = useState('monthly');
  
  // Intro session state
  const [introSession, setIntroSession] = useState(null);
  const [ideaContent, setIdeaContent] = useState('');
  const [submittingIdea, setSubmittingIdea] = useState(false);
  const [participantName, setParticipantName] = useState('');
  const [generatingSession, setGeneratingSession] = useState(false);

  // Track page view on mount
  useEffect(() => {
    trackPageView('landing');
    fetchIntroSession();
    generateRandomName();
  }, []);
  
  const generateRandomName = () => {
    const adjectives = ['Creative', 'Innovative', 'Thoughtful', 'Bright', 'Smart', 'Curious', 'Bold', 'Quick', 'Wise', 'Sharp'];
    const nouns = ['Thinker', 'Ideator', 'Innovator', 'Visionary', 'Explorer', 'Pioneer', 'Genius', 'Mind', 'Creator', 'Builder'];
    const name = `${adjectives[Math.floor(Math.random() * adjectives.length)]} ${nouns[Math.floor(Math.random() * nouns.length)]}`;
    setParticipantName(name);
  };
  
  const fetchIntroSession = async () => {
    try {
      const response = await axios.get(`${API}/sessions/intro/active`);
      if (response.data && response.data.id) {
        setIntroSession(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch intro session:', error);
    }
  };
  
  const handleGenerateSession = async () => {
    setGeneratingSession(true);
    try {
      const response = await axios.get(`${API}/sessions/auto-generate-ideation`);
      if (response.data && response.data.success) {
        toast.success('✨ New topic generated!');
        // Fetch the newly created session
        await fetchIntroSession();
      }
    } catch (error) {
      console.error('Failed to generate session:', error);
      toast.error('Failed to generate topic. Please try again.');
    } finally {
      setGeneratingSession(false);
    }
  };
  
  const handleSubmitIdea = async () => {
    if (!ideaContent.trim()) {
      toast.error('Please enter your idea');
      return;
    }
    
    if (!introSession) {
      toast.error('No active session found');
      return;
    }
    
    setSubmittingIdea(true);
    
    try {
      // Submit the idea
      await axios.post(`${API}/sessions/${introSession.id}/messages`, {
        content: ideaContent.trim(),
        sender_name: participantName,
        participant_id: `landing_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      });
      
      toast.success('✨ Idea submitted! Sign in to see all ideas and contribute more.');
      
      // Redirect to sign in after a short delay
      setTimeout(() => {
        navigate('/login?signup=true');
      }, 1500);
      
    } catch (error) {
      console.error('Failed to submit idea:', error);
      toast.error('Failed to submit idea. Please try again.');
      setSubmittingIdea(false);
    }
  };

  // Track page view on mount
  useEffect(() => {
    trackPageView('landing');
  }, []);

  const features = [
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "Real-Time Ideation",
      description: "Capture ideas from your team in real-time. No more lost sticky notes or forgotten suggestions."
    },
    {
      icon: <Brain className="w-6 h-6" />,
      title: "AI-Powered Insights",
      description: "Let AI automatically cluster and summarize ideas, revealing patterns and key themes instantly."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Easy Participation",
      description: "Participants join with a simple QR code scan. No accounts needed, instant access."
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Smart Questionnaires",
      description: "Create engaging surveys with multiple question types, scoring, and rich media support."
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Flexible Timing",
      description: "Set time limits or end dates. Run sessions that fit your schedule perfectly."
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Beautiful Results",
      description: "Export professional-looking results with AI summaries and visual data presentation."
    }
  ];

  const useCases = [
    {
      title: "For Teachers & Classrooms",
      tagline: "Turn every voice into a spark.",
      description: "Pollar helps students share ideas without fear, giving quiet thinkers the same presence as outspoken ones. Build discussions where everyone feels seen, where curiosity becomes confidence, and where learning is shaped together—not delivered from the front of the room.",
      image: "https://customer-assets.emergentagent.com/job_e454142b-4d2b-4207-9922-d445ac0a7726/artifacts/t4296euy_school.png"
    },
    {
      title: "For Events & Conferences",
      tagline: "Where ideas meet momentum.",
      description: "Whether it's a keynote, a panel, or a hall filled with new connections, Pollar turns passive audiences into active contributors. Capture insights in real time, surface the ideas that move people, and create events participants talk about long after the lights go out.",
      image: "https://customer-assets.emergentagent.com/job_e454142b-4d2b-4207-9922-d445ac0a7726/artifacts/g1rqpnm2_workshop.png"
    },
    {
      title: "For Surveys & Decision Making",
      tagline: "Data that feels human.",
      description: "Go beyond checkboxes and basic forms. Pollar gathers perspectives, not just responses—helping leaders, facilitators, and creators make decisions rooted in what people truly think. Clarity replaces guessing, and teams move forward with shared understanding.",
      image: "https://customer-assets.emergentagent.com/job_e454142b-4d2b-4207-9922-d445ac0a7726/artifacts/ce4gdbef_pm.png"
    },
    {
      title: "AI-Powered Insight Summary",
      tagline: "Find the signal in the noise.",
      description: "Pollar's AI distills hundreds of thoughts into clear themes, revealing patterns that might otherwise remain hidden. No more endless notes, screenshots, or manual sorting. In seconds, you see what matters most—so ideas can turn into action, faster than ever.",
      image: "https://customer-assets.emergentagent.com/job_e454142b-4d2b-4207-9922-d445ac0a7726/artifacts/ulxnl1tv_summary.jpg"
    }
  ];

  const pricingPlans = [
    {
      name: "Free",
      price: "$0",
      period: "forever",
      description: "Perfect for getting started",
      features: [
        "Up to 10 sessions",
        "10 participants per session",
        "5 questions per questionnaire",
        "AI clustering & summaries",
        "QR code generation",
        "Basic analytics"
      ],
      cta: "Start Free",
      popular: false,
      style: "border-gray-200 hover:border-purple-200"
    },
    {
      name: "Premium",
      price: "$7.99",
      originalPrice: "$28",
      period: "per month",
      description: "For professional facilitators",
      features: [
        "Unlimited sessions",
        "100 participants per session",
        "100 questions per questionnaire",
        "All Free features",
        "Advanced question types",
        "Image uploads",
        "End date scheduling",
        "Priority support"
      ],
      cta: "Start Premium",
      popular: true,
      style: "border-purple-500 shadow-xl"
    },
    {
      name: "Lifetime",
      price: "$288",
      originalPrice: null,
      period: "one-time",
      description: "Pay once, own it forever",
      features: [
        "Everything in Premium",
        "Future updates included",
        "No monthly fees",
        "Priority support",
        "Community access",
        "Lifetime license"
      ],
      cta: "Get Lifetime Access",
      popular: false,
      isLifetime: true,
      style: "border-amber-400 hover:border-amber-500 bg-gradient-to-b from-amber-50 to-white"
    }
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    
    // Track section clicks
    if (id === 'features') {
      trackClick('Features', EventTypes.CLICK_FEATURES);
    } else if (id === 'use-cases') {
      trackClick('Use Cases', EventTypes.CLICK_USE_CASES);
    } else if (id === 'pricing') {
      trackClick('Pricing', EventTypes.CLICK_PRICING);
    }
  };

  const handleSignIn = () => {
    trackClick('Sign In', EventTypes.CLICK_SIGN_IN);
    navigate('/login');
  };

  const handleGetStarted = () => {
    trackClick('Get Started', EventTypes.CLICK_GET_STARTED);
    navigate('/login?signup=true');
  };

  const handleStartFree = () => {
    trackClick('Start Free', EventTypes.CLICK_START_FREE);
    navigate('/login?signup=true');
  };

  const handleStartPremium = () => {
    trackClick('Start Premium', EventTypes.CLICK_START_PREMIUM);
    navigate('/login?signup=true');
  };

  const handleStartLifetime = () => {
    trackClick('Start Lifetime', EventTypes.CLICK_START_PREMIUM);
    navigate('/login?signup=true');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="w-12 h-12 flex-shrink-0">
                <PollarLogoSVG style={{ width: '100%', height: '100%' }} />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                Pollar
              </span>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => scrollToSection('features')} className="text-gray-600 hover:text-purple-600 transition">
                Features
              </button>
              <button onClick={() => scrollToSection('use-cases')} className="text-gray-600 hover:text-purple-600 transition">
                Use Cases
              </button>
              <button onClick={() => scrollToSection('pricing')} className="text-gray-600 hover:text-purple-600 transition">
                Pricing
              </button>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={handleSignIn}>
                Sign In
              </Button>
              <Button 
                onClick={handleGetStarted}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Interactive Try It Now Section */}
      <section className="pt-28 pb-12 px-4 bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50">
        <div className="container mx-auto max-w-4xl">
          {!introSession && !generatingSession && (
            /* No active session - Show generate button */
            <div className="text-center">
              <Badge className="mb-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-none">
                <Lightbulb className="w-3 h-3 mr-1" />
                Try It Now - No Sign Up Required
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
                Start a New Conversation
              </h2>
              <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                Click below to generate a thought-provoking topic and share your ideas with the community
              </p>
              <Button
                onClick={handleGenerateSession}
                size="lg"
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-lg px-8 py-6"
              >
                <Sparkles className="mr-2 h-5 w-5" />
                AI Auto Generate Topic
              </Button>
            </div>
          )}
          
          {generatingSession && (
            /* Generating session - Show loading state */
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 mb-4 animate-pulse">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-3 text-gray-900">
                Generating Your Topic...
              </h2>
              <p className="text-gray-600">
                Our AI is selecting a thought-provoking question for you
              </p>
            </div>
          )}
          
          {introSession && !generatingSession && (
            /* Active session exists - Show idea submission form */
            <>
              <div className="text-center mb-8">
                <Badge className="mb-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-none">
                  <Lightbulb className="w-3 h-3 mr-1" />
                  Try It Now - No Sign Up Required
                </Badge>
                <h2 className="text-3xl md:text-4xl font-bold mb-3 text-gray-900">
                  Share Your Idea on This Topic
                </h2>
                <div className="inline-block px-6 py-3 bg-white rounded-2xl shadow-sm border-2 border-purple-200 mb-6">
                  <p className="text-xl md:text-2xl font-semibold text-purple-700">
                    {introSession.topic}
                  </p>
                </div>
              </div>

              <Card className="shadow-xl border-2 border-purple-100">
                <CardContent className="p-6">
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 flex items-center justify-center">
                        <span className="text-white font-semibold text-sm">
                          {participantName.split(' ').map(w => w[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{participantName}</p>
                        <p className="text-xs text-gray-500">Your randomly assigned name</p>
                      </div>
                    </div>
                    
                    <Textarea
                      value={ideaContent}
                      onChange={(e) => setIdeaContent(e.target.value)}
                      placeholder="Type your idea here... Be creative! After submitting, you'll be able to sign in and see all ideas."
                      className="min-h-[120px] resize-none border-2 border-gray-200 focus:border-purple-400"
                      disabled={submittingIdea}
                    />
                  </div>

                  <div className="flex justify-between items-center">
                    <p className="text-sm text-gray-500">
                      <span className="font-semibold text-purple-600">1 idea</span> to unlock full access
                    </p>
                    <Button
                      onClick={handleSubmitIdea}
                      disabled={submittingIdea || !ideaContent.trim()}
                      className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                    >
                      {submittingIdea ? (
                        <>
                          <Clock className="mr-2 h-4 w-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" />
                          Submit Idea
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </section>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-purple-100 text-purple-700 border-purple-300">
              <Sparkles className="w-3 h-3 mr-1" />
              AI-Powered Collaboration
            </Badge>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Transform Ideas Into
              <span className="block bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                Actionable Insights
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Pollar is your all-in-one platform for workshops, brainstorming, and feedback collection. 
              Capture ideas in real-time, let AI organize them, and make better decisions together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                onClick={handleStartFree}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-lg px-8"
              >
                Start For Free
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => scrollToSection('features')}
                className="text-lg px-8"
              >
                See How It Works
              </Button>
            </div>
          </div>

          {/* Hero Product Screenshot */}
          <div className="mt-16 relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl border-8 border-white">
              <video 
                autoPlay
                loop
                muted
                playsInline
                poster="https://customer-assets.emergentagent.com/job_e454142b-4d2b-4207-9922-d445ac0a7726/artifacts/dlunszic_background_4k.jpg"
                className="w-full h-auto"
                onError={(e) => {
                  // Fallback to image if video fails to load
                  e.target.style.display = 'none';
                  const img = document.createElement('img');
                  img.src = 'https://customer-assets.emergentagent.com/job_e454142b-4d2b-4207-9922-d445ac0a7726/artifacts/dlunszic_background_4k.jpg';
                  img.alt = 'Pollar Product Interface';
                  img.className = 'w-full h-auto';
                  e.target.parentNode.appendChild(img);
                }}
              >
                <source 
                  src="https://customer-assets.emergentagent.com/job_login-plus-3/artifacts/k5gbrzik_Speaker.mp4" 
                  type="video/mp4" 
                />
                {/* Fallback for browsers that don't support video tag */}
                <img 
                  src="https://customer-assets.emergentagent.com/job_e454142b-4d2b-4207-9922-d445ac0a7726/artifacts/dlunszic_background_4k.jpg"
                  alt="Pollar Product Interface"
                  className="w-full h-auto"
                />
              </video>
            </div>
            {/* Floating elements */}
            <div className="hidden lg:block absolute -top-8 -left-8 bg-white rounded-2xl shadow-lg p-4 animate-bounce">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                <span className="text-sm font-semibold">Instant Setup</span>
              </div>
            </div>
            <div className="hidden lg:block absolute -bottom-8 -right-8 bg-white rounded-2xl shadow-lg p-4 animate-bounce" style={{ animationDelay: '0.5s' }}>
              <div className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-500" />
                <span className="text-sm font-semibold">AI Insights</span>
              </div>
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="mt-16 flex justify-center animate-bounce">
            <ChevronDown className="w-8 h-8 text-gray-400" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Everything You Need</h2>
            <p className="text-xl text-gray-600">Powerful features that make collaboration effortless</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-2 hover:border-purple-200 hover:shadow-lg transition">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-lg flex items-center justify-center mb-4 text-purple-600">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Video Tutorial Section */}
          <div className="mt-20">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold mb-3">See Pollar in Action</h3>
              <p className="text-lg text-gray-600">Watch our quick tutorial on creating an Idea Pool session</p>
            </div>
            <div className="max-w-4xl mx-auto">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/ew7t1tGvM10"
                  title="Pollar Idea Pool Tutorial"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="py-20 px-4" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Built For Real Teams</h2>
            <p className="text-xl text-gray-600">See how teams use Pollar to drive better outcomes</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-xl transition">
                <div className="aspect-video overflow-hidden border-b">
                  <img 
                    src={useCase.image} 
                    alt={useCase.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold mb-2">{useCase.title}</h3>
                  <p className="text-lg italic text-purple-600 mb-3">{useCase.tagline}</p>
                  <p className="text-gray-600 leading-relaxed">{useCase.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Stop Paying for Raw Data. Pay for Answers.</h2>
            <p className="text-xl text-gray-600">Most tools just collect feedback and leave you to sort the mess. Pollar organizes the noise into insights instantly—standard in every plan.</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mx-auto">
            {pricingPlans.map((plan, index) => (
              <Card 
                key={index} 
                className={`relative overflow-hidden transition border-2 ${plan.style} ${
                  plan.popular ? 'transform scale-105 z-10' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0">
                    <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-1 text-sm font-semibold">
                      MOST POPULAR
                    </div>
                  </div>
                )}
                {plan.isLifetime && (
                  <div className="absolute top-0 right-0">
                    <div className="bg-gradient-to-r from-amber-400 to-orange-500 text-white px-4 py-1 text-sm font-semibold flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> BEST VALUE
                    </div>
                  </div>
                )}
                <CardContent className="p-8 flex flex-col h-full">
                  <div className="mb-6">
                    <h3 className={`text-2xl font-bold mb-2 ${plan.isLifetime ? 'text-amber-800' : ''}`}>{plan.name}</h3>
                    <div className="flex items-baseline gap-2 mb-2">
                      {plan.originalPrice && (
                        <span className="text-xl text-gray-400 line-through decoration-red-500/50 decoration-2 mr-1">
                          {plan.originalPrice}
                        </span>
                      )}
                      <span className={`text-5xl font-bold ${plan.isLifetime ? 'text-amber-600' : ''}`}>{plan.price}</span>
                      <span className="text-gray-500">/{plan.period}</span>
                    </div>
                    <p className="text-gray-600">{plan.description}</p>
                  </div>
                  <ul className="space-y-3 mb-8 flex-grow">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${plan.isLifetime ? 'text-amber-600' : 'text-green-500'}`} />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className={`w-full ${
                      plan.popular 
                        ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700' 
                        : plan.isLifetime 
                          ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white' 
                          : ''
                    }`}
                    variant={plan.popular || plan.isLifetime ? 'default' : 'outline'}
                    size="lg"
                    onClick={() => {
                        if (plan.name === 'Premium') handleStartPremium();
                        else if (plan.isLifetime) handleStartLifetime();
                        else handleStartFree();
                    }}
                  >
                    {plan.popular && <Crown className="w-4 h-4 mr-2" />}
                    {plan.isLifetime && <Infinity className="w-4 h-4 mr-2" />}
                    {plan.cta}
                  </Button>
                  {plan.popular && (
                    <p className="text-center text-sm text-gray-500 mt-4">
                      Sign up or log in to subscribe
                    </p>
                  )}
                  {plan.isLifetime && (
                    <p className="text-center text-sm text-gray-500 mt-4">
                      One-time payment, lifetime access
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-purple-600 to-indigo-600">
        <div className="container mx-auto max-w-4xl text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Workshops?</h2>
          <p className="text-xl mb-8 opacity-90">
            Join teams around the world using Pollar to capture better ideas and make better decisions.
          </p>
          <Button 
            size="lg"
            className="bg-white text-purple-600 hover:bg-gray-100 text-lg px-8"
            onClick={() => navigate('/login?signup=true')}
          >
            Get Started Free
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          <p className="mt-4 text-sm opacity-75">No credit card required • Free forever plan</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8">
                  <PollarLogoSVG />
                </div>
                <span className="text-xl font-bold text-white">Pollar</span>
              </div>
              <p className="text-sm text-gray-400">
                Making collaboration and feedback collection effortless.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => scrollToSection('features')} className="hover:text-white transition">Features</button></li>
                <li><button onClick={() => scrollToSection('use-cases')} className="hover:text-white transition">Use Cases</button></li>
                <li><button onClick={() => scrollToSection('pricing')} className="hover:text-white transition">Pricing</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition">About</a></li>
                <li><a href="#" className="hover:text-white transition">Blog</a></li>
                <li><a href="#" className="hover:text-white transition">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition">Privacy</a></li>
                <li><a href="#" className="hover:text-white transition">Terms</a></li>
                <li><a href="#" className="hover:text-white transition">Security</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2025 Pollar. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;